// A team's colour, as the page is allowed to use it. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE PRIMARY, AT A FIXED LIGHTNESS — AND NEVER THE SECONDARY
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's colour rule, in its own words (`HANDOFF.md`): *"Team colours give
// each card a wash running away→home and colour the accents: logo disc, period
// labels, play ticks, shots and the curve. Each team's primary keeps its hue, but
// lightness is fixed in oklch, so dark primaries (Nuggets navy, Spurs black) still
// read on our dark surfaces. The secondary colour is never used, which keeps
// orange secondaries (Knicks, Suns) from looking like ember."*
//
// Ember is the colour of YOU on this site, and nothing on the hoops page is you
// until GN-6. So the one way an orange could reach this page is a secondary that
// happens to be orange — the Knicks', the Suns' — and the rule that keeps it out is
// not "pick the right one" but "the second one is never read". `toneFor` takes a
// team's `colors` object and reads `.primary` and only `.primary`; the secondary is
// not an argument anywhere in this file.
//
// ⚠️ ONLY THE HUE AND THE CHROMA SURVIVE. A primary is converted to oklch, its
// chroma is capped at 0.16, and its lightness is replaced: 0.68 for an accent that
// has to be read against the dark surface, 0.46 for a solid disc a white label sits
// on, and 0.55 at low alpha for the card's wash. That is why the Nuggets' navy
// (L≈0.2, unreadable on #0b0e11 as a line) and a white primary both come out usable,
// and it is the whole of "at a fixed oklch lightness".
//
// The conversion is the design prototype's own (`oklchOf` and `tone`, copied from
// `D2D Hoops v1.dc.html`), and the test pins its output against that code's output
// for real primaries — not against this file read back.

/** sRGB channel (0..255) to linear light. */
const toLinear = (channel) => {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** `#rrggbb` to oklch chroma and hue (degrees), or `null` for anything else. */
function chromaAndHue(hex) {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null
  const value = parseInt(hex.slice(1), 16)
  const r = toLinear((value >> 16) & 255)
  const g = toLinear((value >> 8) & 255)
  const b = toLinear(value & 255)
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return { chroma: Math.hypot(a, bb), hue: ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360 }
}

/**
 * The four colours a team gets, from its PRIMARY.
 *
 * `accent` for text and thin lines (period labels, play ticks), `solid` for the disc
 * behind a white abbreviation, `wash` and `faint` for the card's gradient (`faint` is
 * the stale card's, the same hue with its voice turned down). `null` when the team
 * has no usable primary — a London side that is not one of the thirty, a malformed
 * value — and the caller draws it neutral rather than inventing one.
 */
export function toneFor(colors) {
  const parsed = chromaAndHue(colors?.primary)
  if (parsed === null) return null
  const chroma = Math.min(parsed.chroma, 0.16).toFixed(3)
  const hue = parsed.hue.toFixed(1)
  return {
    accent: `oklch(0.68 ${chroma} ${hue})`,
    solid: `oklch(0.46 ${chroma} ${hue})`,
    wash: `oklch(0.55 ${chroma} ${hue} / 0.26)`,
    faint: `oklch(0.55 ${chroma} ${hue} / 0.10)`,
  }
}

/**
 * The one colour a chart line, bar or sparkline is drawn in, from a team's PRIMARY, or `null` for a team with none.
 * P6-329c.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE LIGHTNESS FOR BOTH THEMES, NOT TWO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The season design's colour pass (HANDOFF-seasons.md, flags 13 to 15) fixes lightness at 0.68 on a dark page and 0.46 on a light
 * one, so each chart reads in both. This site has one theme today (`index.html` declares `color-scheme: dark` and `app.css`
 * says no light ramp has landed), so a switch on a theme that does not exist would be dead code that reads as handled. The
 * manager's brief asks for ONE palette legible in both, and `SERIES_LIGHTNESS` is it: a lightness at which every team's hue
 * clears 3:1 (the floor for a graphic, WCAG 1.4.11) against the WORST surface of each theme, measured over all thirty
 * primaries by `hoops-season-styles.test.jsx` and not asserted from here.
 *
 * Measured, worst team in brackets, against the dark site's raised surface (`#15191f`) and the design's darkest light surface
 * (`#e7e2d8`):
 *
 *     0.60   dark 4.11 (CLE)   light 2.87 (BOS)    <- the first guess: BOS misses on light
 *     0.58   dark 3.78 (CLE)   light 3.12 (BOS)
 *     0.57   dark 3.63 (CLE)   light 3.25 (BOS)    <- chosen: both clear 3:1 with room
 *     0.56   dark 3.48 (CLE)   light 3.38 (BOS)
 *
 * Only the hue and chroma of the primary survive, as everywhere in this file, and the secondary is never read. Loss bars,
 * area fills and every other tint of the line are THE SAME COLOUR AT A LOWER ALPHA, applied by the stylesheet, so the page
 * needs one value per team and the stylesheet needs none.
 */
export const SERIES_LIGHTNESS = 0.57

export function seriesColor(team) {
  const parsed = chromaAndHue(team?.colors?.primary)
  if (parsed === null) return null
  return `oklch(${SERIES_LIGHTNESS} ${Math.min(parsed.chroma, 0.16).toFixed(3)} ${parsed.hue.toFixed(1)})`
}

/**
 * The custom properties for a graphic that draws BOTH sides of a game: `--hoops-away-line` and `--hoops-home-line`,
 * each team's one chart colour (`seriesColor`, the GN-2c palette). P6-330b.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE STRIP, THE SHOTS AND THE CURVE ARE DRAWN IN THE CHART PALETTE, NOT THE ACCENT TONE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `toneStyle`'s `accent` is fixed at 0.68 lightness, which is right for a word on the dark surface and was chosen for
 * it. A graphic that has to read in BOTH themes is `SERIES_LIGHTNESS`'s (0.57, measured over all thirty primaries to
 * clear 3:1 against the worst surface of each theme), and the brief for the fun layer says the charts are in the
 * style GN-2c established: one palette. So a mark that is a SHAPE (a bar, a dot, a line, a fill) reads `--hoops-*-line`
 * and a mark that is TEXT (a tricode, a split) keeps reading `--hoops-*-accent`. A side with no usable primary sets
 * nothing, and the stylesheet's neutral shows.
 */
export function chartStyle(awayTeam, homeTeam) {
  const style = {}
  const away = seriesColor(awayTeam)
  const home = seriesColor(homeTeam)
  if (away !== null) style['--hoops-away-line'] = away
  if (home !== null) style['--hoops-home-line'] = home
  return style
}

/** A team in the board's table by its numeric id (history files carry `team_id`, not a tricode), or `null`. */
export function teamById(teams, id) {
  if (!Array.isArray(teams) || !Number.isInteger(id)) return null
  return teams.find((team) => team?.id === id) ?? null
}

/**
 * The custom properties for one team on a list row or a chart: its tones as `--hoops-away-*` (the same names the game
 * view sets, so a mark's disc and a wash draw the same colour here as there) and `--hoops-line`, the one chart colour.
 * A team with no usable primary sets nothing and is drawn neutral.
 */
export function teamStyle(team) {
  const style = toneStyle(team, null)
  const line = seriesColor(team)
  if (line !== null) style['--hoops-line'] = line
  return style
}

/**
 * A team found in the table the board came with, by tricode.
 *
 * Looked up by TRICODE, not id: a card carries both, and the tricode is what the
 * plays carry as their `team`, so one key serves the card, the box score and a play's
 * tick. A tricode that is not in the table is `null`, never a default team.
 */
export function teamFor(teams, tricode) {
  if (!Array.isArray(teams) || typeof tricode !== 'string') return null
  return teams.find((team) => team?.tricode === tricode) ?? null
}

/**
 * The custom properties a card (or the game view) sets so that the stylesheet can
 * colour both sides without knowing either team: `--hoops-away-accent`, `-solid`,
 * `-wash`, `-faint`, and the same for home.
 *
 * ⚠️ A SIDE WITH NO TONE SETS NOTHING, so the stylesheet's own fallback (a neutral
 * from the site's tokens) is what shows. Setting a placeholder here would make an
 * unknown team look like a known one.
 *
 * `faded` (P6-329c) turns each side's wash down to its `faint` form: the stale look, the same hue with its voice
 * lowered, for a file the page knows has stopped refreshing. It changes the wash and nothing else.
 */
export function toneStyle(awayTeam, homeTeam, { faded = false } = {}) {
  const style = {}
  for (const [side, team] of [
    ['away', awayTeam],
    ['home', homeTeam],
  ]) {
    const tone = toneFor(team?.colors)
    if (tone === null) continue
    for (const [name, value] of Object.entries(tone)) style[`--hoops-${side}-${name}`] = value
    if (faded) style[`--hoops-${side}-wash`] = tone.faint
  }
  return style
}
